import React, { useState, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import api from '../../../services/api';

const Step1Contact = () => {
    const { mockUser, refreshUser } = useOutletContext();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    
    // Dropdown Data
    const [stateList, setStateList] = useState([]);
    const [districtList, setDistrictList] = useState([]);
    const [permDistrictList, setPermDistrictList] = useState([]);

    // Form Data
    const [formData, setFormData] = useState({
        presAddress: '', presSubLocality: '', presLocality: '', 
        presCountry: 'India', presState: '0', presDistrict: '0', presPincode: '',
        email: '', confirmEmail: '', mobile: '', confirmMobile: '', altMobile: '',
        
        sameAsPresent: true,
        
        permAddress: '', permSubLocality: '', permLocality: '', 
        permCountry: 'India', permState: '0', permDistrict: '0', permPincode: ''
    });

    // 1. Initial Data Load (States + Saved Data)
    // 1. Initial Data Load (States + Saved Data)
    useEffect(() => {
        const init = async () => {
            try {
                // A. Fetch States Lookup
                const stateRes = await api.get('/mock-cuet/states');
                if (stateRes.data.success) setStateList(stateRes.data.data);

                // B. Fetch Saved User Data
                if (mockUser && mockUser.id) {
                    const res = await api.get(`/mock-cuet/application-data?mockAppId=${mockUser.id}`);
                    
                    if (res.data.success) {
                        const dbData = res.data.data;
                        
                        // Map Database Columns (snake_case) to State (camelCase)
                        setFormData(prev => ({
                            ...prev,
                            // Present Address
                            presAddress: dbData.pres_address || '',
                            presSubLocality: dbData.pres_sub_locality || '',
                            presLocality: dbData.pres_locality || '',
                            presCountry: dbData.pres_country || 'India',
                            presState: dbData.pres_state || '0',
                            presDistrict: dbData.pres_district || '0',
                            presPincode: dbData.pres_pincode || '',
                            
                            // Contact
                            email: dbData.email || '',
                            confirmEmail: dbData.email || '', // Auto-fill confirm
                            mobile: dbData.mobile || '',
                            confirmMobile: dbData.mobile || '', // Auto-fill confirm
                            altMobile: dbData.alt_mobile || '',
                            
                            // Permanent Address logic
                            sameAsPresent: dbData.same_as_present === 1, // DB uses 1/0, React uses true/false
                            
                            permAddress: dbData.perm_address || '',
                            permSubLocality: dbData.perm_sub_locality || '',
                            permLocality: dbData.perm_locality || '',
                            permCountry: dbData.perm_country || 'India',
                            permState: dbData.perm_state || '0',
                            permDistrict: dbData.perm_district || '0',
                            permPincode: dbData.perm_pincode || ''
                        }));
                    }
                }
            } catch (err) { 
                console.error("Init Error", err); 
            }
        };
        init();
    }, [mockUser]);

    // 2. Fetch Present Districts when State changes
    useEffect(() => {
        if (formData.presState && formData.presState !== '0') {
            api.get(`/mock-cuet/districts/${formData.presState}`)
                .then(res => setDistrictList(res.data.data || []))
                .catch(err => console.error(err));
        } else {
            setDistrictList([]);
        }
    }, [formData.presState]);

    // 3. Fetch Permanent Districts when State changes
    useEffect(() => {
        if (!formData.sameAsPresent && formData.permState && formData.permState !== '0') {
            api.get(`/mock-cuet/districts/${formData.permState}`)
                .then(res => setPermDistrictList(res.data.data || []))
                .catch(err => console.error(err));
        }
    }, [formData.permState, formData.sameAsPresent]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => {
            const newData = { ...prev, [name]: type === 'checkbox' ? checked : value };
            
            // Logic for "Same as Present"
            if (newData.sameAsPresent && name.startsWith('pres')) {
                const permKey = name.replace('pres', 'perm');
                if (newData.hasOwnProperty(permKey)) newData[permKey] = value;
            }
            return newData;
        });
    };

    const handleCheckbox = (e) => {
        const checked = e.target.checked;
        setFormData(prev => ({
            ...prev,
            sameAsPresent: checked,
            // If checked, copy all fields
            permAddress: checked ? prev.presAddress : prev.permAddress,
            permSubLocality: checked ? prev.presSubLocality : prev.permSubLocality,
            permLocality: checked ? prev.presLocality : prev.permLocality,
            permCountry: checked ? prev.presCountry : prev.permCountry,
            permState: checked ? prev.presState : prev.permState,
            permDistrict: checked ? prev.presDistrict : prev.permDistrict,
            permPincode: checked ? prev.presPincode : prev.permPincode
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        // Basic validations
        if(formData.email !== formData.confirmEmail) { alert("Emails do not match"); setLoading(false); return; }
        if(formData.mobile !== formData.confirmMobile) { alert("Mobile Numbers do not match"); setLoading(false); return; }

        try {
            const res = await api.post('/mock-cuet/save-step', {
                mockAppId: mockUser.id,
                stepNumber: 1,
                stepData: formData,
                nextStepNumber: 2
            });

            if (res.data.success) {
                refreshUser(2); // Update Dashboard state
                navigate('/services/counselling/dashboard/personal-details');
            }
        } catch (err) {
            alert("Error saving data. Please check console.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div id="ctl00_ContentPlaceHolder1_pnlApplicationForm">
            <div className="appstatus" style={{ paddingTop: '0px', marginTop: '0px' }}>
                <div id="ctl00_ContentPlaceHolder1_pnlContactDetails">
                    
                    {/* Header */}
                    <h4>
                        <span id="ctl00_ContentPlaceHolder1_lblContactDetailsSectionHeader">वर्तमान पता (Present Address)</span>
                    </h4>

                    {/* Form Container */}
                    <form className="form-horizontal formLabel" onSubmit={handleSubmit}>
                        
                        {/* Address Field */}
                        <div className="form-group">
                            <label className="control-label col-sm-4">
                                <span>पता (Premises No/ Name)</span>
                            </label>
                            <div className="col-sm-4">
                                <input name="presAddress" value={formData.presAddress} onChange={handleChange} type="text" className="form-control" required />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="control-label col-sm-4">
                                <span>उप-स्थानीयता (Sub-Locality)</span>(Optional)
                            </label>
                            <div className="col-sm-4">
                                <input name="presSubLocality" value={formData.presSubLocality} onChange={handleChange} type="text" className="form-control" />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="control-label col-sm-4">
                                <span>स्थानीयता (Locality)</span>
                            </label>
                            <div className="col-sm-4">
                                <input name="presLocality" value={formData.presLocality} onChange={handleChange} type="text" className="form-control" required />
                            </div>
                        </div>

                        {/* Country */}
                        <div className="form-group">
                            <label className="control-label col-sm-4">
                                <span>देश (Country)</span>
                            </label>
                            <div className="col-sm-4">
                                <select name="presCountry" value={formData.presCountry} onChange={handleChange} className="form-control">
                                    <option value="India">India</option>
                                    <option value="Outside">Outside India</option>
                                </select>
                            </div>
                        </div>

                        {/* State (Dynamic) */}
                        <div className="form-group">
                            <label className="control-label col-sm-4">
                                <span>राज्य (State)</span>
                            </label>
                            <div className="col-sm-4">
                                <select name="presState" value={formData.presState} onChange={handleChange} className="form-control" required>
                                    <option value="0">--Select State--</option>
                                    {stateList.map((st) => (
                                        <option key={st.state_code} value={st.state_code}>{st.state_name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {/* District (Dynamic) */}
                        <div className="form-group">
                            <label className="control-label col-sm-4">
                                <span>जिला (District)</span>
                            </label>
                            <div className="col-sm-4">
                                <select name="presDistrict" value={formData.presDistrict} onChange={handleChange} className="form-control" required>
                                    <option value="0">--Select District--</option>
                                    {districtList.map((dist) => (
                                        <option key={dist.district_code} value={dist.district_code}>{dist.district_name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="control-label col-sm-4">
                                <span>पिन कोड (PIN Code)</span>
                            </label>
                            <div className="col-sm-4">
                                <input name="presPincode" value={formData.presPincode} onChange={handleChange} type="text" maxLength="6" className="form-control" required />
                            </div>
                        </div>

                        {/* Email */}
                        <div className="form-group">
                            <label className="control-label col-sm-4">
                                <span>ईमेल पता (Email Address)</span>
                            </label>
                            <div className="col-sm-4">
                                <input name="email" value={formData.email} onChange={handleChange} type="email" className="form-control" required />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="control-label col-sm-4">
                                <span>Confirm Email Address</span>
                            </label>
                            <div className="col-sm-4">
                                <input name="confirmEmail" value={formData.confirmEmail} onChange={handleChange} type="email" className="form-control" required />
                            </div>
                        </div>

                        {/* Mobile */}
                        <div className="form-group">
                            <label className="control-label col-sm-4">
                                <span>मोबाइल नंबर (Mobile Number)</span>
                            </label>
                            <div className="col-sm-1">
                                <span className="form-control" style={{fontWeight: 'bold'}}>91</span>
                            </div>
                            <div className="col-sm-3">
                                <input name="mobile" value={formData.mobile} onChange={handleChange} type="text" maxLength="10" className="form-control" required />
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="control-label col-sm-4">
                                <span>Confirm Mobile Number</span>
                            </label>
                            <div className="col-sm-1">
                                <span className="form-control" style={{fontWeight: 'bold'}}>91</span>
                            </div>
                            <div className="col-sm-3">
                                <input name="confirmMobile" value={formData.confirmMobile} onChange={handleChange} type="text" maxLength="10" className="form-control" required />
                            </div>
                        </div>

                        {/* --- Permanent Address --- */}
                        <h4 style={{marginTop: '20px'}}>
                            <span>स्थायी पता (Permanent Address)</span>
                        </h4>

                        <div className="checkbox" style={{marginLeft: '20px', marginBottom: '20px', padding: '10px', background: '#fcf8e3', border: '1px solid #faebcc'}}>
                            <label>
                                <input type="checkbox" name="sameAsPresent" checked={formData.sameAsPresent} onChange={handleCheckbox} /> 
                                <span style={{fontWeight:'bold', marginLeft:'5px'}}>Same As Present Address</span>
                            </label>
                        </div>

                        {/* Permanent Address Fields (Disabled/Hidden logic controlled via state or UI) */}
                        <div className="form-group">
                            <label className="control-label col-sm-4"><span>पता (Premises No/ Name)</span></label>
                            <div className="col-sm-4">
                                <input name="permAddress" value={formData.permAddress} onChange={handleChange} type="text" className="form-control" disabled={formData.sameAsPresent} />
                            </div>
                        </div>

                        {/* Add other permanent fields similarly (Locality, State, District) */}
                        {/* We use permState and permDistrictList for the dropdowns here */}
                        <div className="form-group">
                            <label className="control-label col-sm-4"><span>राज्य (State)</span></label>
                            <div className="col-sm-4">
                                <select name="permState" value={formData.permState} onChange={handleChange} className="form-control" disabled={formData.sameAsPresent}>
                                    <option value="0">--Select State--</option>
                                    {stateList.map((st) => (
                                        <option key={st.state_code} value={st.state_code}>{st.state_name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label className="control-label col-sm-4"><span>जिला (District)</span></label>
                            <div className="col-sm-4">
                                <select name="permDistrict" value={formData.permDistrict} onChange={handleChange} className="form-control" disabled={formData.sameAsPresent}>
                                    <option value="0">--Select District--</option>
                                    {permDistrictList.map((dist) => (
                                        <option key={dist.district_code} value={dist.district_code}>{dist.district_name}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        
                         <div className="form-group">
                            <label className="control-label col-sm-4">
                                <span>पिन कोड (PIN Code)</span>
                            </label>
                            <div className="col-sm-4">
                                <input name="permPincode" value={formData.permPincode} onChange={handleChange} type="text" maxLength="6" className="form-control" disabled={formData.sameAsPresent} />
                            </div>
                        </div>

                        {/* CAPTCHA PLACEHOLDER */}
                        <h4>CAPTCHA</h4>
                        <div className="form-group">
                            <label className="control-label col-sm-4">Enter Security Pin</label>
                            <div className="col-sm-4">
                                <input type="text" className="form-control" placeholder="Enter text from image" required />
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="control-label col-sm-4"></label>
                            <div className="col-sm-4">
                                 <img src="/assets/cuet-mock/images/DisplayCaptchaImg.jpg" alt="Captcha" style={{height:'34px'}} />
                                 <img src="/assets/cuet-mock/images/Refresh.png" alt="Refresh" style={{height:'30px', marginLeft:'10px', cursor:'pointer'}} />
                            </div>
                        </div>

                        {/* Footer Buttons */}
                        <div className="row">
                            <div className="form-group">
                                <div className="col-sm-12 text-center">
                                    <input type="submit" value="Save & Next" className="btn btn-primary" disabled={loading} />
                                </div>
                            </div>
                        </div>

                    </form>
                </div>
            </div>
        </div>
    );
};

export default Step1Contact;